<template>
  <QTable
    v-model:pagination="tablePagination"
    v-model:selected="selectedBriefs"
    :columns="columns"
    :loading="loading"
    row-key="id"
    :rows="filteredBriefs"
    :rows-per-page-options="rowPerPageOptions"
    selection="multiple"
    style="height: 450px"
    @request="onRequest"
  >
    <template #top-row>
      <QTr class="filter-row">
        <QTd />
        <QTd>
          <QInput
            v-model="filters.id"
            borderless
            clearable
            dense
            placeholder="Search"
            square
          />
        </QTd>
        <QTd>
          <QInput
            v-model="filters.group"
            borderless
            clearable
            debounce="300"
            dense
            placeholder="Search"
            square
          />
        </QTd>
        <QTd>
          <QInput
            v-model="filters.label"
            borderless
            clearable
            debounce="300"
            dense
            placeholder="Search"
            square
          />
        </QTd>
        <QTd>
          <QInput
            v-model="filters.origin"
            borderless
            clearable
            debounce="300"
            dense
            placeholder="Search"
            square
          />
        </QTd>
      </QTr>
    </template>

    <template #body="props">
      <QTr :props="props">
        <QTd class="wrap-table-content vertical-middle">
          <StreemTooltip v-if="isFromBriefSets(props.row)">
            This brief is part of a brief set, to remove it delete it from the
            brief set.
          </StreemTooltip>
          <QCheckbox
            v-model="props.selected"
            :disable="readonly || isFromBriefSets(props.row)"
          />
        </QTd>
        <QTd
          key="id"
          class="wrap-table-content vertical-middle"
          :props="props"
        >
          {{ props.row.id }}
          <span
            v-if="props.row.primary"
            class="text-green"
          >
            (primary)
          </span>
        </QTd>
        <QTd
          key="group"
          class="wrap-table-content vertical-middle"
          :props="props"
        >
          {{ props.row.group }}
        </QTd>
        <QTd
          key="label"
          class="wrap-table-content vertical-middle"
          :props="props"
        >
          <RestrictedRouterLink :to="briefsPage(props.row.id)">
            {{ props.row.label }}
          </RestrictedRouterLink>
        </QTd>
        <QTd
          key="origin"
          :props="props"
        >
          {{ props.row.origin }}
        </QTd>
      </QTr>
    </template>
  </QTable>
</template>

<script>
import { storeToRefs } from "pinia";

import { tableFilter } from "shared/helpers/object";

import { useUserStore } from "src/stores/user";

export default {
  name: "UserBriefsTable",
  props: {
    briefs: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
    },
    pagination: {
      type: Object,
      default: () => ({}),
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    reverseSelection: {
      type: Boolean,
    },
    readonly: {
      type: Boolean,
    },
  },
  emits: ["update", "update:filters", "update:pagination", "request"],
  setup() {
    const userStore = useUserStore();
    const { organisation } = storeToRefs(userStore);

    return { organisation };
  },
  data() {
    return {
      selectedBriefs: [],
      columns: [
        {
          name: "id",
          required: true,
          align: "left",
          label: "ID",
          field: "id",
          sortable: true,
          style: "width: 10%",
        },
        {
          name: "group",
          required: true,
          align: "left",
          label: "Group",
          field: "group",
          sortable: true,
          style: "width: 30%",
        },
        {
          name: "label",
          required: true,
          align: "left",
          label: "Label",
          field: "label",
          sortable: true,
          style: "width: 30%",
        },
        {
          name: "origin",
          align: "left",
          label: "Origin",
          field: "origin",
          sortable: true,
          style: "width: 30%",
        },
      ],
      tablePagination: {
        rowsPerPage: 50,
      },
      rowPerPageOptions: [25, 50],
    };
  },
  computed: {
    filteredBriefs() {
      const primaryBrief = this.briefs.filter((brief) => brief.primary)[0];

      const remainingBriefs = this.briefs
        ?.filter((brief) => !brief.primary)
        .filter((brief) => tableFilter(brief, this.filters));

      return primaryBrief ? [primaryBrief, ...remainingBriefs] : this.briefs;
    },
  },
  watch: {
    filters: {
      deep: true,
      handler(value) {
        this.$emit("update:filters", value);
      },
    },
    selectedBriefs: {
      handler(val) {
        if (this.reverseSelection) {
          const deselectedBriefs = this.briefs.filter(
            (brief) => !val.includes(brief)
          );

          this.$emit("update", deselectedBriefs);
        } else {
          this.$emit("update", val);
        }
      },
    },
  },
  mounted() {
    if (this.reverseSelection) {
      this.selectedBriefs = this.briefs;
    }
  },
  methods: {
    isFromBriefSets(brief) {
      return brief.origin !== "Individual Brief";
    },
    briefsPage(briefId) {
      return `/organisations/${this.organisation.id}/edit/managed-briefs?id=${briefId}`;
    },
    onRequest({ pagination }) {
      this.$emit("update:pagination", pagination);
      this.$emit("request");
    },
  },
};
</script>

<style lang="scss" scoped>
table {
  .wrap-table-content {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: initial;
    word-wrap: break-word;
    overflow-wrap: break-word;
    font-size: 14px;
  }
}
</style>
